'use client'

import { AppRoutes } from '@repo/web-utils/app-routes'
import { motion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import dynamic from 'next/dynamic'
import { CheckCircleIcon } from 'primitives/icons'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'
import {
  useHasAllAccessQuery,
  useIsUserAllAccessTrialingQuery,
} from '@repo/cf-user/hooks/use-user-data'
import { usePricesForUser } from '@repo/cf-user/hooks/use-prices'
import { usePaymentsCompleteScreenDialog } from 'payments/payments-dialog/hooks/use-payments-complete-screen-dialog'

const IMAGE_UPLOADS_URL = 'https://www.creativefabrica.com/wp-content/uploads'

const LocalizedPrice = dynamic(
  () =>
    import('@repo/web-ui/components/localized-price/localized-price').then(
      mod => mod.LocalizedPrice,
    ),
  {
    ssr: false,
    loading: () => (
      <div className="inline-block h-[15px] w-[40px] animate-pulse rounded bg-gray-800" />
    ),
  },
)

export function HeaderTexts({
  hideTitle = false,
  locale,
}: {
  hideTitle?: boolean
  locale: string
}) {
  const t = useTranslations('pages.promo.yearly')
  const { data: isUserTrialing } = useIsUserAllAccessTrialingQuery()
  const { data: userHasAllAccess } = useHasAllAccessQuery()
  const { getRootProps } = usePaymentsCompleteScreenDialog()
  const { prices } = usePricesForUser()

  // If the payments complete screen dialog is open, we don't want to redirect the user
  const skipRedirect = getRootProps().defaultOpen

  if (!skipRedirect && (userHasAllAccess || isUserTrialing)) {
    window.location.href = addOrReplaceLocaleInUrl(AppRoutes.HOME, locale)
  }

  return (
    <div className="flex w-full flex-col items-center justify-center text-center md:w-[44%]">
      {!hideTitle ? (
        <h5 className="text-secondary-500 text-[16px] font-bold uppercase">{t('header.title')}</h5>
      ) : null}
      <h1 className="my-5 text-[30px] font-bold leading-9 md:text-[36px] md:leading-10">
        {t('header.heading')}
      </h1>
      <h4 className="mb-[10px] text-[16px] font-medium">
        <CheckCircleIcon className="mr-[5px] inline-block" />
        {t.rich('header.reason.1', {
          perMonth: () => <LocalizedPrice value={prices.yearlyPromoMonthlySplit} />,
          billed: () => <LocalizedPrice value={prices.yearlyPromo} />,
        })}
      </h4>
      <h4 className="mb-[10px] text-[16px] font-medium">
        <CheckCircleIcon className="mr-[5px] inline-block" />
        {t('header.reason.2')}
      </h4>
      <h4 className="mb-[10px] text-[16px] font-medium">
        <CheckCircleIcon className="mr-[5px] inline-block" />
        {t('header.reason.3')}
      </h4>
      <a
        href={addOrReplaceLocaleInUrl(AppRoutes.B_YEARLY_PROMO, locale)}
        className="text-cf-white flex h-[47px] items-center justify-center rounded bg-[#3ab561] px-5 text-center text-[18px] font-bold uppercase leading-none tracking-[0.9px] md:h-[67px] md:text-[24px]"
      >
        {t('header.cta')}
      </a>
      <h5 className="mt-5 font-semibold">
        {t.rich('header.cancel', {
          price: () => <LocalizedPrice value={prices.yearlyPromo} />,
        })}
      </h5>
    </div>
  )
}

export function Header({ locale }: { locale: string }) {
  return (
    <div className="flex justify-center p-5 pt-0 md:px-0 md:pb-10">
      <div className="relative hidden h-[470px] flex-col overflow-hidden md:flex md:w-[33%]">
        <motion.div
          className="relative inline-block w-full"
          animate={{ top: ['0px', '-400px'] }}
          transition={{ duration: 12, ease: 'linear', repeat: Infinity }}
        >
          <div className="absolute left-[-30px]">
            <div className="relative left-[-80px] flex gap-5">
              <Image
                src={`${IMAGE_UPLOADS_URL}/2023/01/31/Retro-Beach-Heart-Watercolor-Clipart-Graphics-59528166-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2022/11/29/Saturday-Fonts-49295311-1-312x208.png`}
                width={135}
                height={80}
                alt=""
              />
            </div>
            <div className="relative left-[-180px] mt-5 flex gap-5">
              <Image
                src={`${IMAGE_UPLOADS_URL}/2022/11/03/Thick-Fonts-44593511-1-312x208.png`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2023/02/13/Zodiac-Sign-Bundle-Clipart-Graphics-57473429-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2022/05/16/232961/Font-Bundle-Preview-Image-Bundle-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
            </div>
            <div className="relative left-[-70px] mt-5 flex gap-5">
              <Image
                src={`${IMAGE_UPLOADS_URL}/2023/01/03/Dog-Lover-Watercolor-Collection-Set-Graphics-56031022-312x208.png`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2020/08/06/Underwater-Watercolor-Collection-Graphics-4869554-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2022/12/20/Beautiful-Wildflower-Fonts-53644547-1-1-312x208.png`}
                width={135}
                height={80}
                alt=""
              />
            </div>
            <div className="relative mt-5 flex gap-5">
              <Image
                src={`${IMAGE_UPLOADS_URL}/2023/01/14/Beach-Sunset-Watercolor-Collection-Graphics-57601853-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2021/07/12/The-Joyful-Font-Bundle-Bundles-14616617-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
            </div>
            <div className="relative left-[-70px] mt-5 flex gap-5">
              <Image
                src={`${IMAGE_UPLOADS_URL}/2023/01/31/Daisy-Fonts-49031763-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2021/01/25/Watercolor-Animal-Portraits-and-Flowers-Graphics-8066307-1-312x208.jpeg`}
                width={135}
                height={80}
                alt=""
              />
            </div>
            <div className="relative left-[-80px] mt-5 flex gap-5">
              <Image
                src={`${IMAGE_UPLOADS_URL}/2023/01/31/Retro-Beach-Heart-Watercolor-Clipart-Graphics-59528166-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2022/11/29/Saturday-Fonts-49295311-1-312x208.png`}
                width={135}
                height={80}
                alt=""
              />
            </div>
            <div className="relative left-[-180px] mt-5 flex gap-5">
              <Image
                src={`${IMAGE_UPLOADS_URL}/2022/11/03/Thick-Fonts-44593511-1-312x208.png`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2023/02/13/Zodiac-Sign-Bundle-Clipart-Graphics-57473429-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2022/05/16/232961/Font-Bundle-Preview-Image-Bundle-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
            </div>
            <div className="relative left-[-70px] mt-5 flex gap-5">
              <Image
                src={`${IMAGE_UPLOADS_URL}/2023/01/03/Dog-Lover-Watercolor-Collection-Set-Graphics-56031022-312x208.png`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2020/08/06/Underwater-Watercolor-Collection-Graphics-4869554-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2022/12/20/Beautiful-Wildflower-Fonts-53644547-1-1-312x208.png`}
                width={135}
                height={80}
                alt=""
              />
            </div>
            <div className="relative mt-5 flex gap-5">
              <Image
                src={`${IMAGE_UPLOADS_URL}/2023/01/14/Beach-Sunset-Watercolor-Collection-Graphics-57601853-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2021/07/12/The-Joyful-Font-Bundle-Bundles-14616617-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
            </div>
            <div className="relative left-[-70px] mt-5 flex gap-5">
              <Image
                src={`${IMAGE_UPLOADS_URL}/2023/01/31/Daisy-Fonts-49031763-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2021/01/25/Watercolor-Animal-Portraits-and-Flowers-Graphics-8066307-1-312x208.jpeg`}
                width={135}
                height={80}
                alt=""
              />
            </div>
          </div>
        </motion.div>
        <div className="absolute top-[460px] w-full shadow-[0_0_29px_69px_#fff]" />
      </div>
      <HeaderTexts locale={locale} />
      <div className="relative hidden h-[470px] flex-col overflow-hidden md:flex md:w-[33%]">
        <motion.div
          className="relative inline-block w-full"
          animate={{ top: ['0px', '-400px'] }}
          transition={{ duration: 12, ease: 'linear', repeat: Infinity }}
        >
          <div className="absolute right-[-30px]">
            <div className="relative right-[-80px] flex flex-row-reverse gap-5">
              <Image
                src={`${IMAGE_UPLOADS_URL}/2023/01/31/Daisy-Fonts-49031763-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/uploads/2023/02/02/Cute-Unicorn-Watercolor-Clipart-Bundle-Graphics-59757863-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
            </div>
            <div className="relative right-[-180px] mt-5 flex flex-row-reverse gap-5">
              <Image
                src={`${IMAGE_UPLOADS_URL}/2020/10/08/The-Mega-SVG-Bundle-Bundles-5933358-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2023/01/03/Pink-Vibes-Duo-Fonts-30688189-312x208.png`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2022/11/02/Retro-Christmas-Sublimation-Bundle-Graphics-38296620-312x208.png`}
                width={135}
                height={80}
                alt=""
              />
            </div>
            <div className="relative right-[-70px] mt-5 flex flex-row-reverse gap-5">
              <Image
                src={`${IMAGE_UPLOADS_URL}/2022/12/07/California-Fonts-50882951-1-312x208.png`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2022/10/14/Hello-Angela-Fonts-41406832-312x208.png`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2021/10/21/Urban-Graffiti-Font-Bundle-Bundles-19102008-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
            </div>
            <div className="relative mt-5 flex flex-row-reverse gap-5">
              <Image
                src={`${IMAGE_UPLOADS_URL}/2022/10/05/Carley-Fonts-40212163-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2020/07/18/Celestial-Tarot-Illustrations-Graphics-4661789-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
            </div>
            <div className="relative right-[-70px] mt-5 flex flex-row-reverse gap-5">
              <Image
                src={`${IMAGE_UPLOADS_URL}/2022/12/15/January-Fonts-52598194-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2023/01/07/Watercolor-Cow-And-Flower-Bundle-Clipart-Graphics-56670794-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
            </div>
            <div className="relative right-[-80px] mt-5 flex flex-row-reverse gap-5">
              <Image
                src={`${IMAGE_UPLOADS_URL}/2023/01/31/Daisy-Fonts-49031763-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2023/02/02/Cute-Unicorn-Watercolor-Clipart-Bundle-Graphics-59757863-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
            </div>
            <div className="relative right-[-180px] mt-5 flex flex-row-reverse gap-5">
              <Image
                src={`${IMAGE_UPLOADS_URL}/2020/10/08/The-Mega-SVG-Bundle-Bundles-5933358-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2023/01/03/Pink-Vibes-Duo-Fonts-30688189-312x208.png`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2022/11/02/Retro-Christmas-Sublimation-Bundle-Graphics-38296620-312x208.png`}
                width={135}
                height={80}
                alt=""
              />
            </div>
            <div className="relative right-[-70px] mt-5 flex flex-row-reverse gap-5">
              <Image
                src={`${IMAGE_UPLOADS_URL}/2022/12/07/California-Fonts-50882951-1-312x208.png`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2022/10/14/Hello-Angela-Fonts-41406832-312x208.png`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2021/10/21/Urban-Graffiti-Font-Bundle-Bundles-19102008-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
            </div>
            <div className="relative mt-5 flex flex-row-reverse gap-5">
              <Image
                src={`${IMAGE_UPLOADS_URL}/2022/10/05/Carley-Fonts-40212163-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2020/07/18/Celestial-Tarot-Illustrations-Graphics-4661789-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
            </div>
            <div className="relative right-[-70px] mt-5 flex flex-row-reverse gap-5">
              <Image
                src={`${IMAGE_UPLOADS_URL}/2022/12/15/January-Fonts-52598194-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
              <Image
                src={`${IMAGE_UPLOADS_URL}/2023/01/07/Watercolor-Cow-And-Flower-Bundle-Clipart-Graphics-56670794-1-312x208.jpg`}
                width={135}
                height={80}
                alt=""
              />
            </div>
          </div>
        </motion.div>
        <div className="absolute top-[460px] w-full shadow-[0_0_29px_69px_#fff]" />
      </div>
    </div>
  )
}
